import React from 'react';
import { Helmet } from 'react-helmet-async';
import { TrendingUp, Home } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import NationalLocalFunnel from '../../components/national/NationalLocalFunnel';
import NationalRelatedGuides from '../../components/national/NationalRelatedGuides';

const SITE = 'https://buildpilot.com.au';
const CANONICAL = `${SITE}/granny-flat-costs-australia`;

const BANDS = [
  { tier: 'Kit / prefab studio (~30m²)', range: '$60k - $120k', note: 'Factory-built, delivered and installed. Fastest and most affordable path.' },
  { tier: '1-bedroom built on site (~45m²)', range: '$120k - $180k', note: 'Site-built with a proper kitchen and bathroom. More design flexibility.' },
  { tier: '2-bedroom secondary dwelling (~60m²+)', range: '$180k - $280k+', note: 'Larger footprint, often for rental income or multi-generational living.' },
];

const DRIVERS = [
  'Site access and slope - a crane or difficult access adds thousands',
  'Connecting water, sewer and power to the main dwelling',
  'Council approval pathway and whether it can be rented separately',
  'Finish level, inclusions and whether it needs its own driveway or parking',
];

const LOCAL = [
  { code: 'SA', state: 'South Australia', city: 'Adelaide', primary: { label: 'Adelaide granny flats', to: '/granny-flat-cost-adelaide' }, secondary: { label: 'Granny flat builders', to: '/granny-flat-builders-adelaide' } },
  { code: 'VIC', state: 'Victoria', city: 'Melbourne', primary: { label: 'Melbourne granny flats', to: '/granny-flat-builders-melbourne' }, secondary: { label: 'Granny flat builders', to: '/what-to-look-for-in-a-granny-flat-builder-melbourne' } },
];

const GrannyFlatCostsAustraliaPage = () => {
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Guides', item: `${SITE}/guides` },
      { '@type': 'ListItem', position: 3, name: 'Granny flat costs in Australia', item: CANONICAL },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>How Much Does a Granny Flat Cost in Australia? (2026) | BuildPilot</title>
        <meta name="description" content="A national guide to granny flat and secondary-dwelling costs in Australia for 2026 - kit vs built-on-site ranges, the cost drivers, approval basics, and live local builders for your state." />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content="How Much Does a Granny Flat Cost in Australia? (2026)" />
        <meta property="og:description" content="Kit vs built-on-site cost bands, what drives the price, and live local granny flat builders for your state." />
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <Header variant="light" />

      <main data-testid="national-granny-page">
        <section className="border-b border-gray-100 bg-[#fafbfc]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
            <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#1d8efe] mb-3">National guide · Australia</p>
            <h1 className="text-[32px] sm:text-[44px] font-bold text-[#1d1d1f] tracking-tight leading-[1.1] mb-4">
              How much does a granny flat cost in Australia?
            </h1>
            <p className="text-[16px] sm:text-[18px] text-[#6b7078] leading-[1.6] max-w-2xl">
              A granny flat (or secondary dwelling) can add family space or rental income - but prices range widely by type and site. Here's the national 2026 picture, the costs that catch people out, and where to find builders near you.
            </p>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex items-center gap-2 mb-5">
            <TrendingUp className="w-5 h-5 text-[#1d8efe]" />
            <h2 className="text-[24px] sm:text-[30px] font-bold text-[#1d1d1f] tracking-tight">Granny flat cost by type</h2>
          </div>
          <div className="space-y-3">
            {BANDS.map((b) => (
              <div key={b.tier} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-2xl border border-gray-200 p-5" data-testid={`granny-band-${b.tier.split(' ')[0].toLowerCase().replace(/[^a-z0-9]/g,'')}`}>
                <div className="sm:max-w-[60%]">
                  <h3 className="text-[16px] font-bold text-[#1d1d1f]">{b.tier}</h3>
                  <p className="text-[13.5px] text-[#86868b] mt-0.5 leading-[1.5]">{b.note}</p>
                </div>
                <div className="text-[18px] font-bold gradient-text-blue shrink-0">{b.range}</div>
              </div>
            ))}
          </div>
          <p className="text-[12.5px] text-gray-500 mt-4 leading-[1.55]">National 2026 ranges, build only. Approvals, siteworks and service connections are usually extra.</p>
        </section>

        <section className="bg-[#fafbfc] border-y border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="flex items-center gap-2 mb-5">
              <Home className="w-5 h-5 text-[#1d8efe]" />
              <h2 className="text-[24px] sm:text-[30px] font-bold text-[#1d1d1f] tracking-tight">What drives the price</h2>
            </div>
            <ul className="space-y-2.5">
              {DRIVERS.map((d) => (
                <li key={d} className="flex items-start gap-3 text-[15px] text-[#1d1d1f] leading-[1.55]">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1d8efe]" />{d}
                </li>
              ))}
            </ul>
            <p className="text-[13.5px] text-[#86868b] mt-5 leading-[1.6]">
              Whether a granny flat can be rented out separately - and what approval it needs - is set by your state and council. Check the local pages below before you commit.
            </p>
          </div>
        </section>

        <NationalLocalFunnel
          funnelTestId="granny-local-funnel"
          cardPrefix="granny-local-"
          eyebrow="Local costs & builders"
          heading="Get a real quote and local rules."
          intro="BuildPilot is live in South Australia and Victoria and expanding across Australia. Open your state for local granny flat costs, the approval pathway and experienced builders."
          states={LOCAL}
          crossLink={{ label: 'Weighing it against an extension? See renovation costs', to: '/renovation-costs-australia', testid: 'granny-to-reno-link' }}
        />

        <NationalRelatedGuides currentSlug="/granny-flat-costs-australia" />
      </main>

      <Footer />
    </div>
  );
};

export default GrannyFlatCostsAustraliaPage;
